/** Preferencia push + JWT + device_id en IndexedDB (también lo lee el service worker). */

const DB_NAME = 'shc-push-auth';
const DB_VERSION = 2;
const STORE = 'auth';
const KEY_JWT = 'jwt';
const KEY_DEVICE = 'deviceId';
const KEY_DESIRED = 'desired';
const KEY_AUTOMATIONS = 'desired_automations';
const KEY_PHONE = 'desired_phone';
const LS_DEVICE = 'shc_push_device_id';
const LS_DESIRED = 'shc_push_desired';
const LS_AUTOMATIONS = 'shc_push_desired_automations';
const LS_PHONE = 'shc_push_desired_phone';

export type PushCategory = 'automations' | 'phone';

function categoryLsKey(category: PushCategory): string {
  return category === 'phone' ? LS_PHONE : LS_AUTOMATIONS;
}

function categoryIdbKey(category: PushCategory): string {
  return category === 'phone' ? KEY_PHONE : KEY_AUTOMATIONS;
}

function readLsFlag(key: string): boolean | null {
  try {
    const v = localStorage.getItem(key);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {
    /* noop */
  }
  return null;
}

function writeLsFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* noop */
  }
}

/** Copia el switch viejo (todo ON/OFF) a las dos categorías, una sola vez. */
function migrateLegacySync(): void {
  if (readLsFlag(LS_AUTOMATIONS) !== null || readLsFlag(LS_PHONE) !== null) return;
  const legacy = readLsFlag(LS_DESIRED);
  if (legacy === null) return;
  writeLsFlag(LS_AUTOMATIONS, legacy);
  writeLsFlag(LS_PHONE, legacy);
}

function syncMasterDesiredLs(): void {
  const anyOn = readLsFlag(LS_AUTOMATIONS) === true || readLsFlag(LS_PHONE) === true;
  writeLsFlag(LS_DESIRED, anyOn);
}

/** Lectura síncrona: el usuario quiere al menos una categoría en este dispositivo. */
export function peekPushDesiredSync(): boolean {
  migrateLegacySync();
  return readLsFlag(LS_AUTOMATIONS) === true || readLsFlag(LS_PHONE) === true;
}

/** Lectura síncrona de un switch (automatizaciones o teléfono/timbre). */
export function peekPushCategoryDesiredSync(category: PushCategory): boolean {
  migrateLegacySync();
  return readLsFlag(categoryLsKey(category)) === true;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | null> {
  if (!('indexedDB' in window)) return null;
  try {
    const db = await openDb();
    const value = await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const g = tx.objectStore(STORE).get(key);
      g.onsuccess = () => resolve((g.result as T) ?? null);
      g.onerror = () => reject(g.error);
    });
    db.close();
    return value;
  } catch {
    return null;
  }
}

async function idbSet(key: string, value: unknown | null): Promise<void> {
  if (!('indexedDB' in window)) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      if (value === null || value === undefined) store.delete(key);
      else store.put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* noop */
  }
}

function flagFromIdb(v: boolean | number | string | null): boolean {
  return v === true || v === 1 || v === '1';
}

function newDeviceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** ID estable de este navegador/PWA (sobrevive recargas; no se borra al logout). */
export async function getPushDeviceId(): Promise<string> {
  try {
    const fromLs = localStorage.getItem(LS_DEVICE);
    if (fromLs) {
      await idbSet(KEY_DEVICE, fromLs);
      return fromLs;
    }
  } catch {
    /* noop */
  }
  const fromIdb = await idbGet<string>(KEY_DEVICE);
  if (fromIdb) {
    try { localStorage.setItem(LS_DEVICE, fromIdb); } catch { /* noop */ }
    return fromIdb;
  }
  const id = newDeviceId();
  try { localStorage.setItem(LS_DEVICE, id); } catch { /* noop */ }
  await idbSet(KEY_DEVICE, id);
  return id;
}

async function hydrateCategoryFromIdb(category: PushCategory): Promise<boolean> {
  migrateLegacySync();
  const ls = readLsFlag(categoryLsKey(category));
  if (ls !== null) return ls;
  const desired = flagFromIdb(await idbGet<boolean | number | string>(categoryIdbKey(category)));
  if (desired) writeLsFlag(categoryLsKey(category), true);
  return desired;
}

/** El usuario eligió avisos de esa categoría en este dispositivo. */
export async function getPushCategoryDesired(category: PushCategory): Promise<boolean> {
  return hydrateCategoryFromIdb(category);
}

/** El usuario eligió al menos una categoría (el SW usa este flag). */
export async function getPushDesired(): Promise<boolean> {
  migrateLegacySync();
  const auto = await getPushCategoryDesired('automations');
  const phone = await getPushCategoryDesired('phone');
  if (auto || phone) {
    writeLsFlag(LS_DESIRED, true);
    return true;
  }
  const v = await idbGet<boolean | number | string>(KEY_DESIRED);
  const desired = flagFromIdb(v);
  if (desired) {
    writeLsFlag(LS_DESIRED, true);
    writeLsFlag(LS_AUTOMATIONS, true);
    writeLsFlag(LS_PHONE, true);
  }
  return desired;
}

async function persistMasterDesired(): Promise<void> {
  const anyOn = readLsFlag(LS_AUTOMATIONS) === true || readLsFlag(LS_PHONE) === true;
  writeLsFlag(LS_DESIRED, anyOn);
  await idbSet(KEY_DESIRED, anyOn);
}

export async function setPushCategoryDesired(
  category: PushCategory,
  desired: boolean,
): Promise<void> {
  writeLsFlag(categoryLsKey(category), desired);
  await idbSet(categoryIdbKey(category), desired);
  syncMasterDesiredLs();
  await persistMasterDesired();
}

/** Enciende o apaga las dos categorías (migración / cortar el canal). */
export async function setPushDesired(desired: boolean): Promise<void> {
  writeLsFlag(LS_AUTOMATIONS, desired);
  writeLsFlag(LS_PHONE, desired);
  writeLsFlag(LS_DESIRED, desired);
  await idbSet(KEY_AUTOMATIONS, desired);
  await idbSet(KEY_PHONE, desired);
  await idbSet(KEY_DESIRED, desired);
}

export async function savePushAuthToken(token: string | null): Promise<void> {
  await idbSet(KEY_JWT, token);
  // Asegura device_id disponible para el SW aunque aún no se haya activado push.
  void getPushDeviceId();
}
